/**
 * A date, or a span of them, written the way a person would.
 *
 * The import review screen was printing raw stored values — "start date:
 * 2023-10-30 · end date: 2023-11-03" — which is two labelled ISO strings for
 * one fact, on the screen whose whole job is being read. A run of dates is one
 * thing, so it's said once.
 *
 * Written out and eliding what repeats, because "30 Oct 2023 – 3 Nov 2023"
 * makes you read the year twice to notice it didn't change — but only while
 * that is the account's chosen style. A numeric date does not elide: half of
 * one is not a date. See lib/dateStyle.ts.
 */
import { dateStyle, formatDate as styled } from './dateStyle'

function parse(value: string): Date | null {
  // Date-only strings are parsed as UTC by the spec, so building the parts by
  // hand keeps a 2023-10-30 from becoming the 29th in a western timezone.
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!parts) return null

  const date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

function format(date: Date, options: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString('en-GB', options)
}

const FULL: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }

export function formatDate(value: string): string | null {
  const date = parse(value)
  return date ? styled(date) : null
}

export function formatDateRange(start: string, end?: string | null): string | null {
  const from = parse(start)
  if (!from) return null

  const to = end ? parse(end) : null
  if (!to || to.getTime() === from.getTime()) return styled(from)

  if (dateStyle() !== 'long') return `${styled(from)} – ${styled(to)}`

  // Backwards is a misread column rather than a range, and printing it as one
  // would launder the mistake. Both dates, in the order given.
  if (to < from) return `${styled(from)} – ${styled(to)}`

  const sameYear = from.getFullYear() === to.getFullYear()
  const sameMonth = sameYear && from.getMonth() === to.getMonth()

  if (sameMonth) return `${from.getDate()}–${format(to, FULL)}`
  if (sameYear) return `${format(from, { day: 'numeric', month: 'short' })} – ${format(to, FULL)}`

  return `${format(from, FULL)} – ${format(to, FULL)}`
}
